import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { MaskedLines, FadeIn, Reveal } from "@/components/Motion";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useSeo({
    title: "The Blog — Notes on Stone | Varun Marbles",
    description:
      "Guides, ideas and craft notes on quartz, marble, granite and onyx — written for people building beautiful spaces.",
    path: "/blog",
  });

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-52 pb-28 md:pb-40" data-testid="blog-page">
      <FadeIn delay={0.1}>
        <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-8">
          The Blog — Notes on stone
        </p>
      </FadeIn>
      <h1 className="font-serif font-light tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl max-w-3xl">
        <MaskedLines delay={0.2} lines={[<>Stories in <span className="italic">stone.</span></>]} />
      </h1>
      <FadeIn delay={0.7}>
        <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-muted-foreground">
          Guides, ideas and craft notes on quartz, marble, granite and onyx — written for people
          building beautiful spaces.
        </p>
      </FadeIn>

      <div className="mt-16 md:mt-24">
        {posts === null ? null : posts.length === 0 ? (
          <div className="border border-border p-12 md:p-20 text-center" data-testid="blog-empty-state">
            <p className="font-serif text-3xl font-light tracking-tight mb-3">
              The first story is <span className="italic">being written.</span>
            </p>
            <p className="text-sm font-light text-muted-foreground">
              New articles publish here automatically. Check back soon.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-16" data-testid="blog-list">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 2) * 0.08}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block">
                  {p.image_url && (
                    <div className="overflow-hidden aspect-[16/10] mb-6">
                      <img
                        src={p.image_url}
                        alt={p.title}
                        onError={(e) => {
                          e.currentTarget.parentElement.style.display = "none";
                        }}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-secondary-foreground mb-3">
                    <span>{formatDate(p.published_at)}</span>
                    {p.tags?.slice(0, 2).map((t) => (
                      <span key={t} className="border border-border px-2.5 py-1">{t}</span>
                    ))}
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight leading-snug mb-3 transition-colors duration-300 group-hover:text-foreground/70">
                    {p.title}
                  </h2>
                  {p.meta_description && (
                    <p className="text-sm font-light text-muted-foreground leading-relaxed line-clamp-2">
                      {p.meta_description}
                    </p>
                  )}
                  <span className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase">
                    <span className="border-b border-foreground/40 pb-1 transition-colors duration-300 group-hover:border-foreground">
                      Read story
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
